import type { WidgetConfig } from './config';
import { normalizeAuthIdentity } from './authDisplay';
import { t, getLocale } from './i18n';
import { readStoredCurrencyRaw } from './utils/currency';
import { getBrowserTimezonePayload } from './utils/timezone';
import type { TrackingAcquisition } from './trackingAcquisition';
import { getSessionId, newFormOperationId } from './trackingSession';

const DEFAULT_HEADERS: RequestInit['headers'] = {
  'Content-Type': 'application/json',
};

export interface ApiPayload<T = unknown> {
  status: number;
  result?: T;
  error?: string;
  errors?: Record<string, string[]>;
}

export interface TrackingEventPayload {
  event_name: string;
  schema_version: number;
  event_uuid: string;
  event_ts: number;
  page_path: string;
  page_type: string;
  session_id: string;
  is_preview?: boolean;
  meta?: Record<string, unknown>;
  acquisition?: TrackingAcquisition;
}

export interface TrackingQaClaim {
  expires_at: number;
  signature: string;
}

let trackingIdentityToken = '';

export function setTrackingIdentityToken(token: unknown): void {
  trackingIdentityToken = typeof token === 'string' ? token.trim().slice(0, 4096) : '';
}

export interface TestimonialItem {
  id?: number | string;
  name?: string;
  message?: string;
  date?: string | number;
}

let settingsInFlight: Promise<ApiPayload> | null = null;

function resolveUrl(input: string): URL | null {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(input, base);
  } catch {
    return null;
  }
}

export async function getSettings(config: WidgetConfig): Promise<ApiPayload> {
  if (settingsInFlight) return settingsInFlight;
  settingsInFlight = api(config, '/api/w/settings', { includeReferer: true, retries: 2 });
  try {
    return await settingsInFlight;
  } finally {
    settingsInFlight = null;
  }
}

function getTrackingParams(config: WidgetConfig, opts?: { includeReferer?: boolean }): Record<string, string> {
  const params: Record<string, string> = {};
  // Site identification: server resolves settings by site_key (same as siteKey)
  if (config.siteKey) params.site_key = config.siteKey;
  if (config.previewToken) params.preview = config.previewToken;

  // 1. Inject server-side tracking params from config (session-based)
  if (config.puid) params.puid = config.puid;
  if (config.partner) params.partner = config.partner;
  if (config.cmp) params.cmp = config.cmp;
  if (config.cmpid) params.cmpid = config.cmpid;
  if (config.sub1) params.sub1 = config.sub1;
  if (config.sub2) params.sub2 = config.sub2;
  if (config.sub3) params.sub3 = config.sub3;
  if (config.sub4) params.sub4 = config.sub4;
  if (config.sub5) params.sub5 = config.sub5;

  // 2. Override/Append from URL (if present in browser address bar)
  if (typeof window !== 'undefined') {
    const search = new URLSearchParams(window.location.search);
    const keys = ['site_key', 'puid', 'partner', 'cmp', 'cmpid', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5', 'cur', 'test_lang', 'preview'];
    keys.forEach((key) => {
      const val = search.get(key);
      if (val) params[key] = val;
    });
    if (!params.cur) {
      const storedCur = readStoredCurrencyRaw();
      if (storedCur) params.cur = storedCur;
    }
    // referer only when requested (attribution: first load, addToCart, placeOrder)
    if (opts?.includeReferer && document.referrer) {
      params['referer'] = document.referrer;
    }
  }

  return params;
}

/** Options for api(): includeReferer = send document.referrer for attribution (first load / add / order); skipCache = bypass GET cache (e.g. after add-to-cart) */
export type ApiOptions = RequestInit & { includeReferer?: boolean; retries?: number; skipCache?: boolean };

/** Throttle GET by URL: same URL within this ms reuses last result (stops polling/duplicate calls) */
const GET_THROTTLE_MS = 2000;
const getCache = new Map<string, { p: Promise<ApiPayload>; ts: number }>();

export function invalidateCartGetCache(config: WidgetConfig): void {
  const base = config.apiUrl.replace(/\/$/, '');
  const prefix = `${base}/api/w/cart`;
  for (const key of [...getCache.keys()]) {
    if (key.startsWith(prefix)) getCache.delete(key);
  }
}

/** Drop throttled GET cache for auth/cart (logout must not rehydrate from a cached customer-auth). */
export function invalidateAuthAndCartGetCache(config: WidgetConfig): void {
  const base = config.apiUrl.replace(/\/$/, '');
  const authPrefix = `${base}/api/w/customer-auth`;
  const cartPrefix = `${base}/api/w/cart`;
  for (const key of [...getCache.keys()]) {
    if (key.startsWith(authPrefix) || key.startsWith(cartPrefix)) getCache.delete(key);
  }
}

export function applyCartResultToStore(
  store: { setCart: (count: number, total: number, products: any[]) => void },
  cartData: { products?: any[]; total?: unknown } | null | undefined,
): void {
  if (!cartData || !Array.isArray(cartData.products)) return;
  const products = cartData.products;
  const count = products.reduce((acc: number, p: any) => acc + (Number(p?.amount) || 0), 0);
  const total = parseFloat(String(cartData.total ?? '0').replace(/[^0-9.]/g, ''));
  store.setCart(count, total, products);
}

function isProdLike(): boolean {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h !== 'localhost' && !h.endsWith('.localhost') && h !== '127.0.0.1';
}

/** Hide technical messages (Redis, Connection refused, etc.) in production */
function userFacingError(msg: string | undefined): string {
  if (!msg) return t('api.genericError');
  if (!isProdLike()) return msg;
  const lower = msg.toLowerCase();
  if (/connection refused|tcp:\/\/|redis|6379|exception|pdo|sqlstate|stack trace|fatal error/i.test(lower)) {
    return t('api.genericError');
  }
  return msg;
}

function flattenFirstError(value: unknown, path = ''): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = flattenFirstError(item, path);
      if (found) return found;
    }
  } else if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      const nextPath = path ? `${path}.${key}` : key;
      const found = flattenFirstError(item, nextPath);
      if (found) return nextPath && !found.startsWith(`${nextPath}:`) ? `${nextPath}: ${found}` : found;
    }
  }
  return '';
}

/** Resolve display error: prefer concrete validation text, else t(api.error.{code}) when error_code present. */
function resolveApiError(data: Record<string, unknown>, errMsg: string): string {
  const result = (
    typeof data.result === 'object' && data.result !== null ? data.result : data
  ) as Record<string, unknown>;
  const code = result?.error_code ?? data.error_code;
  const validationMessage = flattenFirstError(data.errors);
  const concreteMessage = validationMessage || errMsg;
  if (typeof code === 'number') {
    const params: Record<string, string | number> = {};
    if (typeof result.limit === 'number') params.limit = result.limit;
    const fallback = userFacingError(concreteMessage);
    if (fallback && fallback !== t('api.genericError')) {
      return fallback;
    }
    const key = `api.error.${code}`;
    const translated = t(key, '');
    if (translated && translated !== key) {
      return t(key, params);
    }
    return fallback;
  }
  return userFacingError(concreteMessage);
}

export function formatApiErrorFromPayload(payload: Record<string, unknown> | null | undefined): string {
  if (!payload) return t('api.genericError');
  const messages = payload.messages as Record<string, string> | undefined;
  const errMsg = (messages && messages['E']) || (payload.error as string) || t('api.genericError');
  return resolveApiError(payload, errMsg);
}

export async function api<T = unknown>(
  config: WidgetConfig,
  path: string,
  options: ApiOptions = {}
): Promise<ApiPayload<T>> {
  const { includeReferer, retries = 1, skipCache = false, ...fetchOpts } = options;
  const base = config.apiUrl.replace(/\/$/, '');
  let url = path.startsWith('/') ? `${base}${path}` : `${base}/api/w/${path}`;

  // Append tracking params (referer only when includeReferer — attribution)
  const tracking = getTrackingParams(config, { includeReferer });
  const urlObj = resolveUrl(url);
  if (!urlObj) {
    return { status: 0, error: t('api.genericError') };
  }
  Object.entries(tracking).forEach(([k, v]) => {
    // Don't overwrite if already in path (unlikely but safe)
    if (!urlObj.searchParams.has(k)) {
      urlObj.searchParams.set(k, v);
    }
  });
  if ((fetchOpts.method ?? 'GET').toUpperCase() !== 'GET') {
    const operationId = newFormOperationId();
    if (operationId) {
      urlObj.searchParams.set('form_operation_id', operationId);
      urlObj.searchParams.set('tracking_session_id', getSessionId());
      urlObj.searchParams.set('client_version', 'shop-widget-observe-20261010-v1');
    }
  }
  url = urlObj.toString();

  const isGet = (fetchOpts.method ?? 'GET') === 'GET' && !fetchOpts.body;
  if (isGet && !skipCache) {
    const cached = getCache.get(url);
    if (cached && Date.now() - cached.ts < GET_THROTTLE_MS) {
      return cached.p as Promise<ApiPayload<T>>;
    }
  }

  const headers = { ...DEFAULT_HEADERS, ...fetchOpts.headers };
  if (fetchOpts.body instanceof FormData) {
    delete (headers as any)['Content-Type'];
  }

  let res!: Response;
  const maxAttempts = Math.max(1, Math.min(retries, 3));
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      res = await fetch(url, { ...fetchOpts, credentials: 'include', headers });
      break;
    } catch (e) {
      if (attempt >= maxAttempts - 1) {
        const msg = (e instanceof TypeError && /fetch|network/i.test(String((e as Error).message)))
          ? t('api.networkError')
          : userFacingError((e as Error)?.message);
        return { status: 0, error: msg };
      }
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
    }
  }

  const data = (await res.json().catch(() => ({}))) as ApiPayload<T> & Record<string, unknown>;
  if (!res.ok) {
    const messages = data.messages as Record<string, string> | undefined;
    const errMsg = (messages && messages['E']) || (data.error as string) || res.statusText;
    return { ...data, status: res.status, error: resolveApiError(data, errMsg) };
  }
  // Backend returns HTTP 200 with payload.status = "FAILURE" etc. on errors
  const payloadStatus = data.status as string | number | undefined;
  if (payloadStatus === 'ERROR' || payloadStatus === 'FAILURE' || payloadStatus === 'NOT_VALID' || payloadStatus === 'NOT_CREATED' || payloadStatus === 'NOT_FOUND') {
    const messages = data.messages as Record<string, string> | undefined;
    const errArr = data.errors as string[] | undefined;
    const errMsg = (messages && (messages['E'] ?? messages['I'])) || (errArr && errArr[0]) || (data.error as string) || 'Request failed';
    return { ...data, status: 520, error: resolveApiError(data, errMsg) };
  }
  if (payloadStatus === 'NOT_AUTHENTICATED') {
    const messages = data.messages as Record<string, string> | undefined;
    const errMsg = (messages && messages['E']) || (data.error as string) || 'Authentication failed';
    return { ...data, status: 401, error: resolveApiError(data, errMsg) };
  }
  const result = { ...data, status: res.status };
  if (isGet && !skipCache) {
    const p = Promise.resolve(result) as Promise<ApiPayload>;
    getCache.set(url, { p, ts: Date.now() });
    setTimeout(() => getCache.delete(url), GET_THROTTLE_MS);
  }
  return result;
}

export async function trackEvents(
  _config: WidgetConfig,
  events: TrackingEventPayload[],
  useBeacon = false,
  qaClaim: TrackingQaClaim | null = null,
): Promise<boolean> {
  if (!events.length) return true;
  const urlObj = resolveUrl('/tracking/events');
  if (!urlObj) return false;
  const body = JSON.stringify({
    events,
    ...(trackingIdentityToken ? { identity_token: trackingIdentityToken } : {}),
    ...(qaClaim ? { qa_claim: qaClaim } : {}),
  });

  // One request per queue attempt. keepalive permits an ACK during page exit;
  // sendBeacon only confirms browser enqueue and cannot acknowledge delivery.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(urlObj.toString(), {
      method: 'POST',
      credentials: 'include',
      keepalive: useBeacon,
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    });
    if (!res.ok) {
      return false;
    }
    const payload = await res.json().catch(() => ({} as Record<string, unknown>));
    const status = String((payload as Record<string, unknown>).status ?? '');
    const code = String((payload as Record<string, unknown>).code ?? '');
    return res.status === 202 && status === 'accepted'
      && (code === 'accepted' || code === 'sampled_passive');
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getCustomerAuth(config: WidgetConfig, opts?: { skipCache?: boolean }) {
  return api<{ login?: string; name?: string; suc_count?: number; bonus_account?: number; tracking_identity_token?: string }>(config, '/api/w/customer-auth', {
    retries: 2,
    ...(opts?.skipCache ? { skipCache: true } : {}),
  });
}

export function formatOrderLimitToast(code: number, limit: number): string {
  if (code === 60101) return t('api.error.60101', { limit });
  if (code === 60103) return t('api.error.60103', { limit });
  return t('api.error.60102', { limit });
}

export interface CartProductLimitMeta {
  max_qty?: number;
  max_qty_items?: number;
  max_qty_pills?: number;
  pack_units?: number;
  max_pills_per_product?: number;
  qty_limit_code?: number;
}

export interface OrderLimits {
  max_products_per_order?: number;
  max_items_per_product?: number;
  max_pills_per_product?: number;
}

export async function getCart(config: WidgetConfig, opts?: { skipCache?: boolean }) {
  return api<{ products?: any[]; total_count?: number; total?: number; order_limits?: OrderLimits; delivery_restrictions?: import('./utils/deliveryEligibility').DeliveryRestriction[] }>(
    config,
    '/api/w/cart',
    { retries: 2, ...opts },
  );
}

export async function getOrders(config: WidgetConfig, params?: Record<string, string>) {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return api<{ orders?: Array<Record<string, unknown>> }>(config, '/api/w/orders' + query);
}

export async function getOrder(config: WidgetConfig, orderId: string) {
  return api<Record<string, unknown>>(config, `/api/w/order/${encodeURIComponent(orderId)}`);
}

export async function getCountries(config: WidgetConfig) {
  return api<Record<string, string>>(config, '/api/w/countries');
}

export async function getStates(config: WidgetConfig) {
  return api<Record<string, Record<string, string>>>(config, '/api/w/states');
}

export async function getAccount(config: WidgetConfig) {
  return api<Record<string, unknown>>(config, '/api/w/account');
}

export async function register(config: WidgetConfig, data: Record<string, unknown>) {
  return api<{ name?: string }>(config, '/api/w/account', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAccount(config: WidgetConfig, data: Record<string, unknown>) {
  return api<{ name?: string }>(config, '/api/w/account', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAccount(config: WidgetConfig, passwd: string, reason: string) {
  return api(config, '/api/w/account', {
    method: 'DELETE',
    body: JSON.stringify({ passwd, reason }),
  });
}

export async function addCoupon(config: WidgetConfig, coupon: string) {
  return api(config, `/api/w/coupon/add/${encodeURIComponent(coupon)}`, { method: 'POST' });
}

export async function clearCoupon(config: WidgetConfig) {
  return api(config, '/api/w/coupon/clear', { method: 'POST' });
}

export async function prepareRetentionOffer(config: WidgetConfig, token: string) {
  return api<{
    offer?: {
      title?: string;
      body_html?: string;
      discount_label?: string;
      coupon_code?: string | null;
      expires_at?: string;
      cta?: string;
    };
    coupon_code?: string | null;
    expires_at?: string | null;
    cta?: string;
  }>(config, '/api/w/retention/offer/prepare', {
    method: 'POST',
    body: JSON.stringify({ token }),
    skipCache: true,
    retries: 1,
  });
}

export async function applyRetentionOffer(config: WidgetConfig, token: string) {
  return api<{ applied?: boolean; coupon_code?: string | null; cta?: string }>(
    config,
    '/api/w/retention/offer/apply',
    {
      method: 'POST',
      body: JSON.stringify({ token }),
      skipCache: true,
      retries: 1,
    },
  );
}

/**
 * Consume abandoned-cart recovery params from URL and ask backend to restore cart.
 * Returns true when backend confirms restoration.
 */
export async function recoverCartFromUrl(config: WidgetConfig): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart_recovery') !== '1') return false;
  const email = params.get('email') || '';
  const shop = params.get('shop') || '';
  const token = params.get('token') || '';
  const ce = params.get('ce') || '';
  const exp = params.get('exp') || '';
  const rn = params.get('rn') || '';
  if (!email || !shop || !token || !exp || !rn) return false;

  const res = await api<{ restored?: boolean }>(config, '/api/w/cart/recover', {
    method: 'POST',
    body: JSON.stringify({ email, shop, token, ce, exp, rn }),
    skipCache: true,
    retries: 1,
  });
  const restored = res.status === 200 && !!res.result?.restored;

  // Remove recovery params from URL to prevent repeat execution on refresh.
  ['cart_recovery', 'email', 'shop', 'token', 'ce', 'exp', 'rn'].forEach((k) => params.delete(k));
  const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, document.title, next);

  return restored;
}

/** Legacy affiliate click row (_partner_clicks) for Bizzlab stats. */
export async function trackCustomer(
  config: WidgetConfig,
  bannerid: number,
  productid: number,
  categoryid: number,
  pay_page: 'Y' | 'N',
) {
  return api<number>(config, '/api/w/track', {
    method: 'POST',
    body: JSON.stringify({
      bannerid,
      productid,
      categoryid,
      pay_page,
    }),
    includeReferer: true,
  });
}

export async function addToCart(config: WidgetConfig, productId: string) {
  const res = await api<{ total_count?: number; products?: any[] }>(config, `/api/w/cart/add/${encodeURIComponent(productId)}`, {
    method: 'POST',
    includeReferer: true,
    skipCache: true,
  });
  if (res.status === 200) {
    invalidateCartGetCache(config);
    const pid = parseInt(productId, 10);
    if (Number.isFinite(pid) && pid > 0) {
      void import('./partnerTracking').then((m) => m.touchPartnerClick(config, { productid: pid }));
    }
  }
  return res;
}

export async function removeFromCart(config: WidgetConfig, productId: string) {
  return api<{ total_count?: number }>(config, `/api/w/cart/update/${encodeURIComponent(productId)}`, {
    method: 'POST',
    body: JSON.stringify({ amount: 0 }),
    skipCache: true,
  });
}

export async function updateCartQuantity(config: WidgetConfig, productId: string, amount: number) {
  const res = await api<{ products?: any[]; total?: unknown; order_limits?: OrderLimits }>(
    config,
    `/api/w/cart/update/${encodeURIComponent(productId)}`,
    {
      method: 'POST',
      body: JSON.stringify({ amount }),
      skipCache: true,
    },
  );
  if (res.status === 200) {
    invalidateCartGetCache(config);
  }
  return res;
}

export async function getPaymentMethods(config: WidgetConfig) {
  return api<Record<string, unknown>[]>(config, '/api/w/payment-methods');
}

export async function getReferralTree(config: WidgetConfig) {
  return api<{ parent_referral: any; referrals: any[] }>(config, '/api/w/referral-tree');
}

export interface ReferralProgramCoupon {
  code: string;
  status: 'active' | 'redeemed' | 'expired' | 'revoked';
  percent: number;
  system: string;
  issued_at: string;
  expires_at: string;
  redeemed_orderid?: number | null;
}

export interface ReferralProgram {
  enabled: boolean;
  referral_link: string;
  referral_code: string;
  offer: { friend_percent: number; reward_percent: number; validity_months: number };
  coupons: ReferralProgramCoupon[];
}

export async function getReferralProgram(config: WidgetConfig) {
  return api<ReferralProgram>(config, '/api/w/referral-program', { skipCache: true });
}

export interface BonusTransactionRow {
  date?: string;
  credit?: number | string;
  debit?: number | string;
  type?: string;
  sorderid?: string;
  [key: string]: unknown;
}

export async function getBonusTransactions(config: WidgetConfig) {
  return api<BonusTransactionRow[]>(config, '/api/w/bonus-transactions');
}

export interface NewsListItem {
  listid: number;
  name: string;
  descr?: string;
  subscribed: 'Y' | 'N';
}

export async function getNewsLists(config: WidgetConfig, email: string) {
  return api<NewsListItem[]>(config, `/api/w/newslists/${encodeURIComponent(email)}`);
}

export async function updateSubscription(
  config: WidgetConfig,
  data: {
    email: string;
    newsListIds: Record<string, string | boolean>;
    sw_honey?: string;
    sw_form_ts?: number;
  }
) {
  return api<number>(config, '/api/w/update-subscription', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getOldQnr(config: WidgetConfig) {
  return api<Record<string, unknown>>(config, '/api/w/old-qnr');
}

/** pm: flat object for payment details (e.g. card_type, card_number for CC; CheckNumber, BankName, etc. for echeque) */
export async function setPaymentMethod(
  config: WidgetConfig,
  paymentId: string,
  pm?: Record<string, string>
) {
  return api<{ total_count?: number }>(config, '/api/w/cart/set/payment-method', {
    method: 'POST',
    body: JSON.stringify({ paymentid: paymentId, ...pm }),
  });
}

export async function setQnr(config: WidgetConfig, qnrData: Record<string, unknown>) {
  return api<{ total_count?: number }>(config, '/api/w/cart/set/qnr', {
    method: 'POST',
    body: JSON.stringify(qnrData),
  });
}

export async function setBonusProduct(config: WidgetConfig, bonusProductId: string) {
  return api<{ total_count?: number }>(config, '/api/w/cart/set/bonus', {
    method: 'POST',
    body: JSON.stringify({ bonus_productid: Number(bonusProductId) || 0 }),
  });
}

/** Pre-order (like static checkout page) so cart gets orderid before final place-order */
export async function placePreOrder(config: WidgetConfig) {
  return api<string>(config, '/api/w/place-pre-order', { method: 'POST', body: '{}' });
}

/** Defaults for QNR fields required by backend qnrCheck (same as ms2024 CartService before placeOrder) */
const QNR_DEFAULTS: Record<string, string> = {
  h101: 'no', h102: '', h103: 'no', h104: 'no', h105: 'no', h106: 'no',
  h202: 'no', h203: 'good', h204: 'no', h205: 'no', h206: '', h208: 'no',
  h209: 'no', h210: 'no', h211: 'no', h212: 'no', h213: 'no', h214: 'no',
  h215: 'no', h216: 'no', h217: 'no', h218: 'no',
  q19: 'no', q20: 'no', q21: 'no', q22: 'no',
};

/** Build full qnr (defaults + raw, p1 from bd_*) so backend qnrCheck passes. Use for setQnr and place-order. */
export function buildFullQnr(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = { ...QNR_DEFAULTS };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const pad2 = (s: string) => (s.length === 1 ? '0' + s : s);
  const bd_Day = String(raw.bd_Day ?? '').trim();
  const bd_Month = String(raw.bd_Month ?? '').trim();
  const bd_Year = String(raw.bd_Year ?? '').trim();
  if (bd_Day && bd_Month && bd_Year) {
    out.p1 = `${pad2(bd_Day)}/${pad2(bd_Month)}/${bd_Year}`;
  } else if (raw.p1 && typeof raw.p1 === 'string') {
    out.p1 = raw.p1;
  }
  for (const k of ['p2', 'p3', 'p4', 'h201', 'h219', 'h220', 'h221', 'h222', 'h223']) {
    if (raw[k] != null && raw[k] !== '') out[k] = String(raw[k]).trim();
  }
  return out;
}

/** pm and qnr from store so backend has them if session is not shared (e.g. cross-origin).
 * retentionConsent: when true, backend upserts shop_subscribe_leads with trigger_type=checkout.
 * locale: from widgetConfig.locale, for email resolution. */
export async function placeOrder(
  config: WidgetConfig,
  pm?: Record<string, string> | null,
  qnr?: Record<string, unknown> | null,
  opts?: { retentionConsent?: boolean }
) {
  const qnrPayload = buildFullQnr(qnr);
  const body: Record<string, unknown> = { pm: pm || {}, qnr: qnrPayload, ...getBrowserTimezonePayload() };
  if (opts?.retentionConsent) body.retention_consent = true;
  if (config.locale) body.locale = config.locale;
  return api<{ sorderid?: string; payment_link?: string } | string>(config, '/api/w/place-order', {
    method: 'POST',
    body: JSON.stringify(body),
    includeReferer: true,
  });
}

export async function login(
  config: WidgetConfig,
  login: string,
  password: string
) {
  return api<{ name?: string }>(config, '/api/w/login', {
    method: 'POST',
    body: JSON.stringify({ login, password, site_key: config.siteKey }),
  });
}

/** Step 1: send 10-char reset code to email */
export async function sendResetCode(
  config: WidgetConfig,
  email: string,
  antiBot?: { sw_honey?: string; sw_form_ts?: number }
) {
  return api(config, 'send-reset-code', {
    method: 'POST',
    body: JSON.stringify({ email, ...(antiBot ?? {}) }),
  });
}

/** Step 2: set new password with email + code */
export async function resetPassword(config: WidgetConfig, email: string, resetCode: string, passwd: string) {
  return api(config, 'reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, reset_code: resetCode, passwd }),
  });
}

export async function getTickets(config: WidgetConfig, page: number = 1) {
  return api<{ tickets: any[]; total: number; per_page: number }>(config, `/api/w/tickets/${page}`);
}

const TICKET_DETAIL_CACHE_TTL = 60_000;
const ticketDetailCache = new Map<string, { res: ApiPayload<any>; ts: number }>();

export async function getTicket(config: WidgetConfig, encId: string) {
  const cached = ticketDetailCache.get(encId);
  if (cached && Date.now() - cached.ts < TICKET_DETAIL_CACHE_TTL) return cached.res;
  const res = await api<any>(config, `/api/w/ticket/${encodeURIComponent(encId)}`);
  if (res.status === 200 && res.result) ticketDetailCache.set(encId, { res, ts: Date.now() });
  return res;
}

export async function resolveTicket(config: WidgetConfig, encId: string) {
  return api(config, `/api/w/ticket/${encodeURIComponent(encId)}/resolve`);
}

export async function createTicket(config: WidgetConfig, subject: string, message: string, files: File[], shopUid?: string) {
  const formData = new FormData();
  formData.append('subject', subject);
  formData.append('message', message);
  const timezone = getBrowserTimezonePayload();
  formData.append('customer_timezone', timezone.customer_timezone);
  formData.append('customer_timezone_offset_minutes', String(timezone.customer_timezone_offset_minutes));
  const uid = (shopUid ?? config.siteKey ?? '').trim();
  if (uid) {
    formData.append('shop_uid', uid);
  }
  files.forEach((file) => formData.append('attachments[]', file));

  return api(config, '/api/w/ticket', {
    method: 'POST',
    body: formData,
  });
}

export async function replyTicket(config: WidgetConfig, encId: string, message: string, files: File[]) {
  const formData = new FormData();
  formData.append('message', message);
  files.forEach((file) => formData.append('attachments[]', file));

  return api(config, `/api/w/ticket/${encodeURIComponent(encId)}`, {
    method: 'POST',
    body: formData,
  });
}

export function getTicketAttachmentUrl(config: WidgetConfig, encId: string, attachmentId: string) {
  const base = config.apiUrl.replace(/\/$/, '');
  return `${base}/api/w/ticket_attachment/${encodeURIComponent(encId)}/${encodeURIComponent(attachmentId)}`;
}

/** POST /aff-api/builder/shops/{siteKey}/subscribe-events — subscribe_shown, subscribe_dismissed. Fire-and-forget. */
export function subscribeEvents(
  config: WidgetConfig,
  eventType: 'subscribe_shown' | 'subscribe_dismissed',
  meta?: { trigger_type?: string; source_page?: string; device?: string; utm?: Record<string, string>; locale?: string; session_id?: string }
) {
  if (typeof window === 'undefined') return;
  const loc = window.location;
  const body = {
    event_type: eventType,
    session_id: meta?.session_id ?? null,
    trigger_type: meta?.trigger_type ?? null,
    source_page: meta?.source_page ?? (loc.pathname || '/'),
    device: window.innerWidth >= 768 ? 'desktop' : 'mobile',
    utm: meta?.utm ?? (() => {
      const u: Record<string, string> = {};
      const p = new URLSearchParams(loc.search);
      const v = p.get('utm_source'); if (v) u.source = v;
      const m = p.get('utm_medium'); if (m) u.medium = m;
      const c = p.get('utm_campaign'); if (c) u.campaign = c;
      return u;
    })(),
    locale: meta?.locale ?? getLocale() ?? config.locale ?? null,
  };
  fetch(`${config.apiUrl.replace(/\/$/, '')}/aff-api/builder/shops/${encodeURIComponent(config.siteKey)}/subscribe-events`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

/** POST /aff-api/builder/shops/{siteKey}/subscribe-leads — retention opt-in (module 10). */
export async function subscribeLeads(
  config: WidgetConfig,
  payload: {
    email?: string;
    phone?: string;
    trigger_type: string;
    source_page: string;
    consent: boolean;
    session_id?: string;
    utm?: Record<string, string>;
    customer_login?: string;
    is_authenticated?: boolean;
  }
) {
  const body = {
    ...payload,
    locale: getLocale() || config.locale || 'en',
    session_id: payload.session_id ?? '',
    utm: payload.utm ?? {},
    customer_login: payload.customer_login ?? null,
    is_authenticated: !!payload.is_authenticated,
  };
  return api<{ ok?: boolean; lead_id?: string; double_opt_in_required?: boolean; coupon?: { code: string; expires_at: string } }>(
    config,
    `/aff-api/builder/shops/${encodeURIComponent(config.siteKey)}/subscribe-leads`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export async function checkHelpdeskAuth(config: WidgetConfig, authCode: string) {
  return api(config, `/api/w/tickets-check-auth-code/${encodeURIComponent(authCode)}`, {
    method: 'POST'
  });
}

export async function sendHelpdeskAuth(config: WidgetConfig) {
  return api(config, '/api/w/tickets-send-auth-code');
}

export async function getHelpdeskStatus(config: WidgetConfig) {
  return api<string>(config, '/api/w/tickets-get-auth');
}

export async function getTestimonials(config: WidgetConfig) {
  return api<TestimonialItem[]>(config, '/api/w/testimonials');
}

export async function postTestimonial(config: WidgetConfig, payload: { name: string; message: string }) {
  return api(config, '/api/w/testimonial', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

const REFRESH_MIN_INTERVAL_MS = 10_000; // не чаще раза в 10 сек
let refreshInFlight: Promise<void> | null = null;
let lastRefreshAt = 0;
let refreshGeneration = 0;

export async function refreshAuthAndCart(
  config: WidgetConfig,
  store: {
    setAuth: (v: any) => void;
    setCart: (count: number, total: number, products: any[]) => void;
  },
  opts?: { force?: boolean }
) {
  const force = !!opts?.force;
  const now = Date.now();
  if (!force && now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) {
    return refreshInFlight ?? Promise.resolve();
  }
  if (!force && refreshInFlight) return refreshInFlight;
  lastRefreshAt = now;
  const generation = ++refreshGeneration;
  const run = (async () => {
    const bust = force;
    const [authRes, cartRes] = await Promise.all([
      getCustomerAuth(config, bust ? { skipCache: true } : undefined),
      getCart(config, bust ? { skipCache: true } : undefined),
    ]);
    if (generation !== refreshGeneration) return;
    setTrackingIdentityToken((authRes.result as Record<string, unknown> | undefined)?.tracking_identity_token);
    store.setAuth(normalizeAuthIdentity(authRes.result as Record<string, unknown>));

    const cartData = cartRes.result as any;
    const products = cartData?.products ?? [];
    const count = products.reduce((acc: number, p: any) => acc + (p.amount || 0), 0);
    const total = parseFloat(String(cartData?.total || '0').replace(/[^0-9.]/g, ''));

    if (generation !== refreshGeneration) return;
    store.setCart(count, total, products);
  })();
  refreshInFlight = run;
  try {
    await run;
  } finally {
    if (refreshInFlight === run) refreshInFlight = null;
  }
}

/** Cancel in-flight refresh so a late customer-auth cannot overwrite an intentional logout. */
export function cancelAuthCartRefresh(): void {
  refreshGeneration += 1;
  refreshInFlight = null;
  lastRefreshAt = 0;
  setTrackingIdentityToken('');
}

export interface ChatStatusResult {
  online: boolean;
  operators?: number;
}

export async function getChatStatus(config: WidgetConfig): Promise<ChatStatusResult> {
  try {
    const res = await api<ChatStatusResult>(config, '/api/w/chat/status', { retries: 1 });
    if (res.status === 200 && res.result) {
      return res.result;
    }
  } catch { /* swallow — treat as unknown */ }
  return { online: false };
}

export interface TelegramNotificationOption {
  key: keyof TelegramNotificationPrefs;
  label: string;
  description: string;
}

export interface TelegramNotificationPrefs {
  order_status: boolean;
  shipped: boolean;
  tracking_updates: boolean;
  payment: boolean;
  promotions: boolean;
}

export interface TelegramStatusResponse {
  connected: boolean;
  available?: boolean;
  username?: string | null;
  connected_at?: string | null;
  bound_from_site_id?: number | null;
  bound_from_site_code?: string | null;
  bound_from_site_title?: string | null;
  bot_username?: string | null;
  notifications_enabled?: boolean;
  notification_prefs?: TelegramNotificationPrefs;
  notification_options?: TelegramNotificationOption[];
  coupon_offer?: TelegramCouponOffer;
}

export type CouponOfferState = 'ineligible' | 'eligible' | 'active' | 'redeemed' | 'expired';

export interface TelegramCouponOffer {
  state: CouponOfferState;
  reason?: string;
  grant_id?: number;
  source?: 'self' | 'email' | 'agent';
  origin?: string;
  percent?: number;
  minimum?: number;
  system?: string;
  expires_at?: string;
  code?: string;
  redeemed_orderid?: number | null;
}

export interface TelegramConnectResponse {
  bot_url: string;
  tg_app_url?: string;
  token: string;
  expires_in: number;
  expires_at?: string;
}

export async function getTelegramStatus(config: WidgetConfig) {
  return api<TelegramStatusResponse>(config, '/api/w/telegram/status', { skipCache: true });
}

export async function createTelegramConnectLink(
  config: WidgetConfig,
  origin: 'shop_checkout' | 'shop_account' | 'shop_order' | 'onehelp_dashboard',
) {
  return api<TelegramConnectResponse>(config, '/api/w/telegram/connect', {
    method: 'POST',
    body: JSON.stringify({ origin }),
    skipCache: true,
  });
}

export async function applyTelegramConnectCoupon(
  config: WidgetConfig,
  coupon: string,
  mode: 'self_checkout' | 'self_explicit',
  replace_existing: boolean,
) {
  return api<unknown>(config, `/api/w/coupon/add/${encodeURIComponent(coupon)}`, {
    method: 'POST',
    body: JSON.stringify({ mode, replace_existing }),
    skipCache: true,
  });
}

export async function updateTelegramNotifications(
  config: WidgetConfig,
  notification_prefs: TelegramNotificationPrefs,
) {
  return api<Pick<TelegramStatusResponse, 'notifications_enabled' | 'notification_prefs' | 'notification_options'>>(
    config,
    '/api/w/telegram/notifications',
    {
      method: 'PUT',
      body: JSON.stringify({ notification_prefs }),
      skipCache: true,
    },
  );
}

export async function disconnectTelegram(config: WidgetConfig) {
  return api<unknown>(config, '/api/w/telegram', { method: 'DELETE', skipCache: true });
}
